import { forwardRef, useImperativeHandle } from "react";
import { Trans, useTranslation } from "react-i18next";
import { DeleteIconsOptions, DownloadIcon, EyeIcon, IconClip } from "../../../components/buttons/IconButtons";
import CustomCard from "../../../components/CustomCard";
import { CustomSwitch } from "../../../components/CustomSwitch";
import { CustomTextField } from "../../../components/CustomTextField";
import CustomUserChip from "../../../components/CustomUserChip";
import DivContainerBody from "../../../components/DivContainerBody";
import DividerWithText from "../../../components/DividerWithText";
import CustomPhotoPickerMultiple from "../../../components/PhotoPicker/CustomPhotoPickerMultiple";
import { fileSize } from "../../../constant/variable";
import { FILE_TYPE } from "../../../Helpers/fileType";
import { POSITION_MODEL } from "../../../Helpers/PositionModel";
import ManageApp from "../Restrictions/Functionality/ManageApp";
import NotificationChannelsTable from "./Manage/subComponents/NotificationChannelsTable";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 */
const Manage = forwardRef((props, ref) => {
    const {
        photoPickerFile,
        setPhotoPickerFile,
        item,
        emptyFields,
        handleChange,
        handleUpdate,
        isRequiredFields,
        disabled
    } = ManageApp();

    const { t } = useTranslation()

    /** Expose internal function to parent through ref */
    useImperativeHandle(ref, () => ({
        handleUpdate
    }));


    return (
        <DivContainerBody>
            <div className="row">
                <div className="col-12 col-lg-4">
                    <div className={"row"}>
                        <CustomCard
                            classCard={`background-shades-50 border-radius-8px`}
                            classContent={"p-0"}
                            classes={"p-0"}
                            children={
                                <div className={"row g-3 mb-3"}>
                                    <div className={"border-bottom-shades-300 p-3 d-flex align-items-center"}>
                                        <div className={"mr-2"}><EyeIcon width={24} height={24} /></div>
                                        <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("user_preview")}</p>
                                    </div>
                                    <div className={"col-12"}>
                                        <div className="row">
                                            <div className="pb-3">
                                                <CustomUserChip
                                                    className="m-0 chip-primary-200-50 w-100 p-0 justify-content-start p-3"
                                                    name={
                                                        <Trans
                                                            i18nKey="booking_opens_value_days_in_advance"
                                                            values={{
                                                                days: `${item.bookingWindowValue === ""
                                                                    ? 0
                                                                    : item.bookingWindowValue}`
                                                            }}
                                                            components={{
                                                                strong: <strong/>,
                                                            }}
                                                        />
                                                    }
                                                    variant="large"
                                                    iconPosition={POSITION_MODEL.LEFT}
                                                    classNameIcon="icon-color-secondary-700"
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="pb-3">
                                                <CustomUserChip
                                                    className="m-0 chip-primary-200-50 w-100 p-0 justify-content-start p-3"
                                                    name={
                                                        <Trans
                                                            i18nKey="maximum_value_attendees_per_reservation"
                                                            values={{
                                                                value: `${item.attendeesLimitsValue === ""
                                                                    ? 0
                                                                    : item.attendeesLimitsValue}`
                                                            }}
                                                            components={{
                                                                strong: <strong/>
                                                            }}
                                                        />
                                                    }
                                                    variant="large"
                                                    iconPosition={POSITION_MODEL.LEFT}
                                                    classNameIcon="icon-color-secondary-700"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                        />
                    </div>
                    <div className={"row mt-4"}>
                        <CustomCard
                            classCard={`background-shades-50 border-radius-8px`}
                            classContent={"p-0"}
                            classes={"p-0"}
                            children={
                                <div className={"row g-3 mb-3"}>
                                    <div className={"border-bottom-shades-300 p-3 d-flex align-items-center"}>
                                        <div className={"mr-2"}><IconClip width={24} height={24} /></div>
                                        <p className={"font-color-general-950 ibl-font-w-600 fs-14-to-13 "}>{t("policy_documents")}</p>
                                    </div>
                                    <div className={"col-12"}>
                                        <div className="row">
                                            <CustomPhotoPickerMultiple
                                                id='fp_m_fine_document'
                                                photoPickerFile={photoPickerFile}
                                                setPhotoPickerFile={setPhotoPickerFile}
                                                accept={{ ...FILE_TYPE.FILES, size: fileSize }}
                                                showBtnDeleteItemGallery={true}
                                                showBtnCamera={false}
                                                deleteIcon={DeleteIconsOptions}
                                                downloadIcon={DownloadIcon}
                                                showBtnDownload={true}
                                                apiDelete={false}
                                                disabled={disabled}
                                            />
                                        </div>
                                    </div>
                                </div>
                            }
                        />
                    </div>

                </div>
                <div className="col-sm-12 col-lg-8" >
                    <>
                        <DividerWithText title={t('reservation_policies')} />
                        <div className="row pt-3 minh-144px position-relative">
                            <div className="col-12 col-lg-4">
                                <p className="fs-14px font-color-shades-950 ibl-font-w-600">{t('booking_window')}</p>
                                <p className="font-color-shades-950 fs-14px pt-4">{t('control_how_far_in_advance_users_can_book_before_the_event_starts')}</p>
                            </div>
                            <div className="col-12 col-lg-5 pt-4 pt-lg-0 d-flex align-items-end justify-content-start pb-2 mb-2" >
                                <CustomTextField
                                    id={"txt_m_booking_window_value"}
                                    length={3}
                                    name={"bookingWindowValue"}
                                    value={item.bookingWindowValue}
                                    trans={"days_before_start"}
                                    onChange={handleChange}
                                    required={isRequiredFields.isReqBooking}
                                    emptyFields={emptyFields}
                                    type="number"
                                    disabled={disabled || !isRequiredFields.isReqBooking}
                                />
                            </div>
                            <div className='position-absolute w-25 t-10px r-3px'>
                                <CustomSwitch
                                    id="cb_m_booking_window_enabled"
                                    showBorder={false}
                                    name="bookingWindowEnabled"
                                    checked={item.bookingWindowEnabled === true}
                                    onChange={(e) => handleChange({ target: { name: e.target.name, value: item.bookingWindowEnabled } })}
                                    disabled={disabled}
                                />
                            </div>
                        </div>
                        <hr className="border-bottom-shades-300" />
                        <div className="row pt-3 minh-144px position-relative">
                            <div className="col-12 col-lg-4">
                                <p className="fs-14px font-color-shades-950 ibl-font-w-600">{t('attendees_limits')}</p>
                                <p className="font-color-shades-950 fs-14px pt-4">{t('set_maximum_capacity_per_reservation')}</p>
                            </div>
                            <div className="col-12 col-lg-5 pt-4 pt-lg-0 d-flex align-items-end justify-content-start pb-2 mb-2" >
                                <CustomTextField
                                    id={"txt_m_max_attendees"}
                                    length={2}
                                    name={"attendeesLimitsValue"}
                                    value={item.attendeesLimitsValue}
                                    trans={"maximum_attendees_per_reservation"}
                                    onChange={handleChange}
                                    required={isRequiredFields.isReqAttendeesLimit}
                                    emptyFields={emptyFields}
                                    type="number"
                                    disabled={disabled || !isRequiredFields.isReqAttendeesLimit}
                                />
                            </div>
                            <div className='position-absolute w-25 t-10px r-3px'>
                                <CustomSwitch
                                    id="cb_m_is_attendees_limit"
                                    showBorder={false}
                                    name="attendeesLimitsEnabled"
                                    checked={item.attendeesLimitsEnabled === true}
                                    onChange={(e) => handleChange({ target: { name: e.target.name, value: item.attendeesLimitsEnabled } })}
                                    disabled={disabled}
                                />
                            </div>
                        </div>
                    </>
                    <div className="pb-5">
                        <DividerWithText title={t('notification_channels')} />
                        <NotificationChannelsTable
                            item={item}
                            handleChange={handleChange}
                            disabled={disabled}
                        />
                    </div>
                </div>

            </div>

        </DivContainerBody>
    );
})
export default Manage
